import { useEffect, useState } from "react";
import { Loader2, ImagePlus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { translations } from "../i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { key: "hero", fields: ["hero_overline", "hero_line1", "hero_line2", "hero_line3", "hero_sub"] },
  { key: "strip", fields: ["strip_title", "strip_text"] },
  { key: "b2b", fields: ["b2b_title", "b2b_sub"] },
  { key: "story", fields: ["story_title", "story_p1", "story_p2"] },
  { key: "testimonials", fields: ["testimonials_title", "testimonials_score", "testimonials_scoreLabel"] },
];

const defaultFor = (key, lang) => {
  const t = translations[lang];
  const map = {
    hero_overline: t.hero.overline,
    hero_line1: t.hero.lines[0],
    hero_line2: t.hero.lines[1],
    hero_line3: t.hero.lines[2],
    hero_sub: t.hero.sub,
    strip_title: t.strip.title,
    strip_text: t.strip.text,
    b2b_title: t.b2b.title,
    b2b_sub: t.b2b.sub,
    story_title: t.story.title,
    story_p1: t.story.p1,
    story_p2: t.story.p2,
    testimonials_title: t.testimonials.title,
    testimonials_score: t.testimonials.score,
    testimonials_scoreLabel: t.testimonials.scoreLabel,
  };
  return map[key] || "";
};

const AdminHomepage = () => {
  const { t, refreshContent, contentImage } = useShop();
  const d = t.adminHome;
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroFile, setHeroFile] = useState(null);
  const [storyFile, setStoryFile] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setFields(r.data?.fields || {}))
      .catch(() => setFields({}))
      .finally(() => setLoading(false));
  }, []);

  const val = (key, lang) => fields[`${key}_${lang}`] ?? "";
  const setVal = (key, lang, v) =>
    setFields((f) => ({ ...f, [`${key}_${lang}`]: v }));

  const uploadImage = async (section, file) => {
    const data = new FormData();
    data.append("section", section);
    data.append("file", file);
    await axios.post(`${API}/admin/content/image`, data, { withCredentials: true });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      GROUPS.forEach((g) =>
        g.fields.forEach((key) => {
          if (key === "testimonials_score") {
            payload[key] = fields[key] ?? "";
          } else {
            payload[`${key}_tr`] = val(key, "tr") || defaultFor(key, "tr");
            payload[`${key}_en`] = val(key, "en") || defaultFor(key, "en");
          }
        })
      );
      await axios.patch(`${API}/admin/content`, { fields: payload }, { withCredentials: true });
      if (heroFile) await uploadImage("hero", heroFile);
      if (storyFile) await uploadImage("story", storyFile);
      setHeroFile(null);
      setStoryFile(null);
      toast.success(d.saved);
      refreshContent();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : d.error);
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-[11px] tracking-[0.25em] uppercase text-brown-muted";

  const ImageRow = ({ section, file, setFile, label }) => (
    <div className="flex items-center gap-4 flex-wrap">
      <p className={`${labelCls} w-32 shrink-0`}>{label}</p>
      {contentImage(section) && !file && (
        <img src={contentImage(section)} alt="" className="w-16 h-16 object-cover border border-brown/10" />
      )}
      <label
        className="inline-flex items-center gap-3 px-5 py-3 border border-dashed border-brown/25 text-sm text-brown-light hover:border-olive hover:text-olive-dark cursor-pointer transition-colors duration-300"
        data-testid={`content-image-upload-${section}`}
      >
        <ImagePlus size={16} strokeWidth={1.5} />
        {file ? file.name : d.uploadImage}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </label>
      <p className="text-xs text-brown-muted font-light">{d.imageHint}</p>
    </div>
  );

  if (loading) return <p className="text-brown-muted font-light">{t.auth.loading}</p>;

  return (
    <form onSubmit={handleSave} data-testid="homepage-form">
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{d.tab}</h1>
      <p className="mt-4 text-base font-light text-brown-light max-w-xl">{d.sub}</p>

      <div className="mt-10 flex flex-col gap-6">
        {GROUPS.map((g) => (
          <div key={g.key} className="border border-brown/10 bg-cream-dark p-8" data-testid={`content-group-${g.key}`}>
            <h2 className="font-serif text-2xl font-medium text-brown mb-6">{d.groups[g.key]}</h2>
            <div className="flex flex-col gap-6">
              {g.fields.map((key) => (
                <div key={key}>
                  {key === "testimonials_score" ? (
                    <>
                      <p className={`${labelCls} mb-1`}>{d.labels[key]}</p>
                      <Input
                        value={fields[key] ?? defaultFor(key, "tr")}
                        onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                        className="editorial-input"
                        data-testid={`content-field-${key}`}
                      />
                    </>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {["tr", "en"].map((lang) => (
                        <div key={lang}>
                          <p className={`${labelCls} mb-1`}>
                            {d.labels[key]} ({lang.toUpperCase()})
                          </p>
                          {["strip_text", "story_p1", "story_p2", "hero_sub", "b2b_sub"].includes(key) ? (
                            <Textarea
                              rows={3}
                              value={val(key, lang) || defaultFor(key, lang)}
                              onChange={(e) => setVal(key, lang, e.target.value)}
                              className="editorial-input resize-none"
                              data-testid={`content-field-${key}-${lang}`}
                            />
                          ) : (
                            <Input
                              value={val(key, lang) || defaultFor(key, lang)}
                              onChange={(e) => setVal(key, lang, e.target.value)}
                              className="editorial-input"
                              data-testid={`content-field-${key}-${lang}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {g.key === "hero" && (
                <ImageRow section="hero" file={heroFile} setFile={setHeroFile} label={d.heroImage} />
              )}
              {g.key === "story" && (
                <ImageRow section="story" file={storyFile} setFile={setStoryFile} label={d.storyImage} />
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={saving}
        className="mt-8 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
        data-testid="content-save-button"
      >
        {saving && <Loader2 size={16} className="animate-spin" />}
        {saving ? d.saving : d.save}
      </button>
    </form>
  );
};

export default AdminHomepage;
